// ============================================================================
// lib/remote/remoteHost.ts — мост к НАТИВНОМУ Android-хосту «Удалённого доступа»
// (Capacitor-плагин RemoteHost). Телефон отдаёт экран и принимает управление.
// Живёт только на Android (Capacitor): в вебе/десктопе методы недоступны —
// наличие проверяется через isRemoteHostSupported().
// ============================================================================

import { Capacitor, registerPlugin } from '@capacitor/core';

/**
 * Транспорт хоста:
 *  • 'livekit'  — WebRTC/LiveKit (нативный Android-хост);
 *  • 'contract' — наш wire-контракт v2: нативный хост слушает порт (LAN);
 *  • 'relay'    — РЕЛЕЙ сервера за NAT: хост соединяется ИСХОДЯЩИМ (браузерный
 *                 хост lib/remote/contractHost; нативный плагин 'relay' пока
 *                 не принимает — отдельная задача).
 */
export type RemoteHostTransport = 'livekit' | 'contract' | 'relay';

export interface RemoteHostStatus {
  running: boolean;
  sharing: boolean;
  controlOn: boolean;
  controlRequested: boolean;
  sessionId: string;
  room: string;
  /** Активный транспорт: 'livekit' | 'contract'. */
  transport: string;
  /** Пресет качества показа: 'auto' | 'high' | 'medium' | 'low'. */
  quality: string;
  /** Адрес для зрителя у контрактного транспорта (ws://…); у LiveKit — пусто. */
  endpoint: string;
  error: string;
  viewers: number;
}

export interface StartRemoteHostOptions {
  /**
   * Транспорт сеанса:
   *  • 'livekit'  — WebRTC/LiveKit (нужны url+token комнаты remote_{sessionId});
   *  • 'contract' — наш wire-контракт v2 поверх WebSocket (хост слушает порт,
   *    медиа — MediaProjection+MediaCodec H.264, видео-пакеты и JSON те же,
   *    что у нативного движка; см. plans/remote-protocol-contract.md).
   */
  transport: RemoteHostTransport;
  /** Адрес LiveKit (wss/ws) — только transport='livekit'. */
  url?: string;
  /** Access-токен комнаты — только transport='livekit'. */
  token?: string;
  /** Имя комнаты LiveKit (remote_{sessionId}). */
  room: string;
  /** Идентификатор сеанса (u-<hex>) — общий для обоих транспортов. */
  sessionId: string;
  /** Пресет качества (auto|high|medium|low) — контрактный транспорт. */
  quality?: string;
  /** Порт прослушивания контрактного хоста (0/пусто — 8765). */
  port?: number;
  /** Общий секрет контрактного сеанса (пусто — сгенерируется на устройстве). */
  contractToken?: string;
}

export interface RemoteHostListenerHandle {
  remove: () => Promise<void>;
}

interface RemoteHostPluginApi {
  startRemoteHost(options: StartRemoteHostOptions): Promise<{ started: boolean; alreadyRunning?: boolean }>;
  stopRemoteHost(): Promise<void>;
  isRunning(): Promise<RemoteHostStatus>;
  allowControl(): Promise<void>;
  denyControl(): Promise<void>;
  isAccessibilityEnabled(): Promise<{ enabled: boolean }>;
  openAccessibilitySettings(): Promise<void>;
  addListener(eventName: 'status', listenerFunc: (status: RemoteHostStatus) => void): Promise<RemoteHostListenerHandle>;
}

const RemoteHost = registerPlugin<RemoteHostPluginApi>('RemoteHost');

/** Плагин доступен только в нативном Android-приложении (Capacitor). */
export function isRemoteHostSupported(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

export default RemoteHost;
